import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

interface SubscriptionStatusData {
  subscription_status: string;
  has_access: boolean;
  trial_start_date?: string;
  trial_end_date?: string;
  days_remaining?: number;
  total_articles_read: number;
  daily_article_count: number;
  message: string;
}

const SubscriptionStatus: React.FC = () => {
  const [status, setStatus] = useState<SubscriptionStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setStatus(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch subscription status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  if (error || !status) {
    return null;
  }

  // Only show banner when trial or subscription has expired
  const shouldShowBanner =
    status.subscription_status === 'TRIAL_EXPIRED' ||
    status.subscription_status === 'SUBSCRIPTION_EXPIRED';

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div className="rounded-lg border p-4 bg-red-50 border-red-200 text-red-800">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">
            {status.subscription_status === 'TRIAL_EXPIRED' ? 'Free Trial Expired' : 'Subscription Expired'}
          </h3>
          <p className="text-sm mt-1">
            Your {status.subscription_status === 'TRIAL_EXPIRED' ? 'free trial' : 'subscription'} has ended.
            Please contact your school administrator to subscribe to daily current affairs content.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;